import { useCallback, useMemo, useState } from 'react'

import type {
  BillingInterval,
  BillingPlan,
  CurrentPlanSummary,
} from '@/components/billing/billing.interface'
import { useSubscription } from '@/hooks/billing/use-subscription.hook'
import { useSubscriptionState } from '@/hooks/billing/use-subscription-state.hook'

const buildPlanSummary = (
  plan: BillingPlan,
  subscription: { renewalAt: string | null; cancelAt: string | null },
  defaultCard: CurrentPlanSummary['defaultCard']
): CurrentPlanSummary => ({
  plan,
  billingInterval: 'monthly',
  renewalDate: subscription.renewalAt ? new Date(subscription.renewalAt) : new Date(),
  cancelAt: subscription.cancelAt ? new Date(subscription.cancelAt) : null,
  defaultCard,
})

const isFreePlanCheck = (plan: BillingPlan): boolean =>
  plan.monthlyPrice === 0 && plan.yearlyPrice === 0

export interface UseSubscriptionViewReturn {
  planList: BillingPlan[]
  isLoading: boolean
  error: string | null
  isSubscribing: boolean
  subscription: { cancelAt: string | null; subscriptionsEnabled: boolean }
  currentPlanSummary: CurrentPlanSummary | null
  isFreePlan: boolean
  isModalOpen: boolean
  setIsModalOpen: (open: boolean) => void
  handleSelectPlan: (plan: BillingPlan, billingInterval: BillingInterval) => Promise<void>
}

export const useSubscriptionView = (
  onClientSecret: (clientSecret: string, planName: string, isYearly: boolean) => void
): UseSubscriptionViewReturn => {
  const { planList, isLoading, error, subscribe, cancelSubscription, isSubscribing } =
    useSubscription(onClientSecret)
  const { subscription, defaultCard, isCurrentPlan } = useSubscriptionState()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const currentPlan = useMemo(
    () => planList.find((plan) => isCurrentPlan(plan)) ?? null,
    [planList, isCurrentPlan]
  )
  const currentPlanSummary = useMemo(
    () => (currentPlan ? buildPlanSummary(currentPlan, subscription, defaultCard) : null),
    [currentPlan, subscription, defaultCard]
  )
  const isFreePlan = currentPlan ? isFreePlanCheck(currentPlan) : true

  const handleSelectPlan = useCallback(
    async (plan: BillingPlan, billingInterval: BillingInterval): Promise<void> => {
      const isPlanFree = isFreePlanCheck(plan)
      const success = isPlanFree
        ? await cancelSubscription()
        : await subscribe(plan.name, billingInterval === 'yearly')
      if (success) setIsModalOpen(false)
    },
    [subscribe, cancelSubscription]
  )

  return {
    planList,
    isLoading,
    error,
    isSubscribing,
    subscription: {
      cancelAt: subscription.cancelAt,
      subscriptionsEnabled: subscription.subscriptionsEnabled,
    },
    currentPlanSummary,
    isFreePlan,
    isModalOpen,
    setIsModalOpen,
    handleSelectPlan,
  }
}
